/**
 * PATH: packages/ui/src/components/settings/profile-tab/AvatarUploader.tsx
 * PURPOSE: Avatar display, upload, and removal for profile settings
 * WHY: Extracted from ProfileTab.tsx to keep files under 300 lines
 *
 * FLOW:
 * ┌──────────────┐    ┌─────────────┐    ┌──────────────┐
 * │ ProfileData  │ →  │ AvatarUpload│ →  │ onAvatarChg  │
 * └──────────────┘    └─────────────┘    └──────────────┘
 */

import { useCallback } from 'react'
import { Camera, Building2 } from 'lucide-react'
import type { ProfileData } from './profile-tab-types'

interface AvatarUploaderProps {
  profile: ProfileData
  editable: boolean
  onAvatarChange?: (file: File) => Promise<void>
  onAvatarRemove?: () => Promise<void>
}

/** Derive display name and initials from profile data */
function deriveDisplayInfo(profile: ProfileData) {
  const displayName =
    [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'User'
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
  return { displayName, initials }
}

export function AvatarUploader({
  profile,
  editable,
  onAvatarChange,
  onAvatarRemove,
}: AvatarUploaderProps) {
  const { displayName, initials } = deriveDisplayInfo(profile)

  const handleAvatarUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file && onAvatarChange) {
        await onAvatarChange(file)
      }
    },
    [onAvatarChange],
  )

  return (
    <div className="flex items-start gap-6 pb-6 border-b border-slate-200 dark:border-slate-800">
      <div className="relative group">
        {profile.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt={displayName}
            className="w-20 h-20 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white text-xl font-semibold">
            {initials}
          </div>
        )}

        {editable && onAvatarChange && (
          <label className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
            <Camera className="w-5 h-5 text-white" />
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </label>
        )}
      </div>

      <div className="flex-1">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          {displayName}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {profile.email}
        </p>
        {profile.organisation_name && (
          <div className="flex items-center gap-1.5 mt-2 text-sm text-slate-500 dark:text-slate-400">
            <Building2 className="w-4 h-4" />
            <span>{profile.organisation_name}</span>
          </div>
        )}

        {editable && onAvatarRemove && profile.avatar_url && (
          <button
            onClick={onAvatarRemove}
            className="mt-3 text-xs text-red-500 hover:text-red-600 transition-colors"
          >
            Remove photo
          </button>
        )}
      </div>
    </div>
  )
}
